// admin/src/pages/audit/AuditLogs.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Container, Row, Col, Table, Button, Modal, Spinner, Alert,
  InputGroup, FormControl, Pagination, Dropdown,
} from "react-bootstrap";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { Colors } from "../../constants/Colors";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const ITEMS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 400;

/* ---------------- Design tokens ---------------- */
const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover:
    "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ---------------- Filter options ---------------- */
const ROLE_OPTIONS = [
  { value: "All", label: "All Roles" },
  { value: "SuperAdmin", label: "Super Admin" },
  { value: "Operator", label: "Operator" },
  { value: "Passenger", label: "Passenger" },
];

const ACTION_OPTIONS = [
  { value: "All", label: "All Actions" },
  { value: "SIGN_IN", label: "Sign In" },
  { value: "SIGN_OUT", label: "Sign Out" },
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
  { value: "VIEW", label: "View" },
];

/* ---------------- Meta maps ---------------- */
const ROLE_META = {
  SuperAdmin: {
    color: Colors.SECONDARY_BLUE,
    bg: `${Colors.SECONDARY_BLUE}1f`,
    label: "Super Admin",
  },
  Operator: {
    color: Colors.INFO,
    bg: `${Colors.INFO}1f`,
    label: "Operator",
  },
  Passenger: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Passenger",
  },
};

const ACTION_META = {
  SIGN_IN: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Sign In",
    icon: "bi-box-arrow-in-right",
  },
  SIGN_OUT: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "Sign Out",
    icon: "bi-box-arrow-right",
  },
  CREATE: {
    color: Colors.SECONDARY_BLUE,
    bg: `${Colors.SECONDARY_BLUE}1f`,
    label: "Create",
    icon: "bi-plus-circle",
  },
  UPDATE: {
    color: Colors.INFO,
    bg: `${Colors.INFO}1f`,
    label: "Update",
    icon: "bi-pencil",
  },
  DELETE: {
    color: Colors.RED,
    bg: `${Colors.RED}1f`,
    label: "Delete",
    icon: "bi-trash",
  },
  VIEW: {
    color: Colors.GRAY,
    bg: `${Colors.GRAY}26`,
    label: "View",
    icon: "bi-eye",
  },
};

const STATUS_META = {
  success: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Success",
  },
  failure: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Failure" },
  warning: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "Warning",
  },
};

const FALLBACK_META = {
  color: Colors.GRAY,
  bg: `${Colors.GRAY}26`,
  label: "Unknown",
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/);
  return (
    ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?"
  );
};

const getRoleMeta = (role) =>
  ROLE_META[role] || { ...FALLBACK_META, label: role || "Unknown" };

const getActionMeta = (action) =>
  ACTION_META[action] || {
    ...FALLBACK_META,
    label: action || "Unknown",
    icon: "bi-question-circle",
  };

const getStatusMeta = (status) =>
  STATUS_META[status] || {
    ...FALLBACK_META,
    label: (status || "unknown").toUpperCase(),
  };

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    return new Date(timestamp).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "Invalid date";
  }
};

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return "[Unserializable metadata]";
  }
};

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    /* ---- Page shell ---- */
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    /* ---- Header ---- */
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }
    .bk-header-actions { position: relative; z-index: 1; }
    .bk-header-btn { background: rgba(255,255,255,.18) !important; border: 1px solid rgba(255,255,255,.32) !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; backdrop-filter: blur(8px); transition: all .18s; }
    .bk-header-btn:hover:not(:disabled) { background: rgba(255,255,255,.28) !important; transform: translateY(-1px); color: #fff !important; }
    .bk-header-btn:disabled { opacity: .6 !important; cursor: not-allowed !important; transform: none !important; }

    /* ---- Card ---- */
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Toolbar & inputs ---- */
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-search { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; height: auto !important; }
    .bk-search:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-search-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; height: auto !important; min-height: 42px; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    /* ---- Table ---- */
    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; cursor: pointer; }
    .bk-table tbody tr:hover, .bk-table tbody tr:focus { background: ${Colors.LIGHT_GRAY}55; outline: none; }
    .bk-table tbody td.col-chevron { width: 1%; text-align: right; }

    /* ---- Pills & chips ---- */
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .bk-pill.no-dot::before { display: none; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    /* ---- Actor cell ---- */
    .bk-actor-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
    .bk-actor-avatar { width: 30px; height: 30px; border-radius: 8px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 11px; display: grid; place-items: center; flex-shrink: 0; }

    /* ---- Mono text ---- */
    .bk-mono { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 12px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Empty state ---- */
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    /* ---- Skeleton ---- */
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* ---- Buttons ---- */
    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }

    /* ---- Info sections ---- */
    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }
    .bk-info-value.mono { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 12px; font-weight: 500; }

    /* ---- Metadata code block ---- */
    .bk-pre { background: ${Colors.LIGHT_GRAY}; border-radius: 8px; padding: 12px 14px; font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 12px; color: ${Colors.TEXT_DARK}; max-height: 240px; overflow: auto; margin: 0; white-space: pre-wrap; word-break: break-word; }

    /* ---- Modal ---- */
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    /* ---- Pagination ---- */
    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    /* ---- Dropdown ---- */
    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }

    /* ---- Spin ---- */
    .spin { animation: bk-spin 1s linear infinite; display: inline-block; }
    @keyframes bk-spin { to { transform: rotate(360deg); } }

    /* ---- Row focus ---- */
    .bk-row-clickable:focus-visible { box-shadow: inset 0 0 0 2px ${Colors.PRIMARY}; }
  `}</style>
);

/* ================================================================== */
/* ERROR BOUNDARY                                                     */
/* ================================================================== */

class ErrorBoundary extends React.Component {
  state = { hasError: false, errorKey: 0 };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("AuditLogs ErrorBoundary:", error, info);
  }

  reset = () =>
    this.setState((s) => ({ hasError: false, errorKey: s.errorKey + 1 }));

  render() {
    if (this.state.hasError) {
      return (
        <div className="bk-page">
          <Styles />
          <Container fluid>
            <Alert
              variant="danger"
              className="d-flex align-items-center justify-content-between"
            >
              <span>
                <i className="bi bi-exclamation-triangle me-2" />
                An error occurred in Audit Logs.
              </span>
              <Button size="sm" variant="outline-danger" onClick={this.reset}>
                Try again
              </Button>
            </Alert>
          </Container>
        </div>
      );
    }
    return (
      <React.Fragment key={this.state.errorKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

const RolePill = ({ role }) => {
  const m = getRoleMeta(role);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

const ActionPill = ({ action }) => {
  const m = getActionMeta(action);
  return (
    <span
      className="bk-pill no-dot"
      style={{ color: m.color, background: m.bg }}
    >
      <i className={`bi ${m.icon}`} style={{ fontSize: 11 }} />
      {m.label}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const m = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const AuditLogs = () => {
  const navigate = useNavigate();

  /* ---------------- Data state ---------------- */
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  /* ---------------- Filters ---------------- */
  const [roleFilter, setRoleFilter] = useState("All");
  const [actionFilter, setActionFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  /* ---------------- Pagination ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  /* ---------------- Access control ---------------- */
  const [isAdmin, setIsAdmin] = useState(null);

  /* ---------------- Detail modal ---------------- */
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  /* ---------------- Refs ---------------- */
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const fetchAuditLogs = useCallback(
    async (overrides = {}) => {
      const role = overrides.role ?? roleFilter;
      const action = overrides.action ?? actionFilter;
      const search = overrides.search ?? searchQuery;
      const page = overrides.page ?? currentPage;
      const silent = overrides.silent ?? false;

      /* Cancel any in-flight request */
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const myId = ++requestIdRef.current;

      if (silent) setRefreshing(true);
      else setLoading(true);
      setError("");

      try {
        const response = await api.get("/admin/audit-logs", {
          signal: controller.signal,
          params: {
            page,
            per_page: ITEMS_PER_PAGE,
            role: role !== "All" ? role : undefined,
            action: action !== "All" ? action : undefined,
            search: search?.trim() || undefined,
          },
        });

        if (myId !== requestIdRef.current) return; /* stale */

        const data = response.data;
        const rows = Array.isArray(data.logs) ? data.logs : [];
        setLogs(rows);
        setTotalPages(data.pagination?.last_page || 1);
        setTotalItems(data.pagination?.total || 0);
      } catch (err) {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED")
          return;
        if (myId !== requestIdRef.current) return; /* stale */

        // eslint-disable-next-line no-console
        console.error("Audit log fetch error:", err);

        if (err.response?.status === 401) {
          localStorage.removeItem("arkila_admin_token");
          navigate("/login");
          return;
        }

        const msg =
          err.response?.status === 403
            ? "You must be a Super-Admin to view audit logs."
            : err.response?.data?.message ||
              "Failed to load audit logs. Please try again.";

        setError(msg);
        setLogs([]);
        setTotalPages(1);
        setTotalItems(0);
      } finally {
        if (myId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [roleFilter, actionFilter, searchQuery, currentPage, navigate]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setCurrentPage(1);
        fetchAuditLogs({ search: value, page: 1 });
      }, SEARCH_DEBOUNCE_MS),
    [fetchAuditLogs]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  useEffect(() => () => abortRef.current?.abort(), []);

  /* ================================================================ */
  /* BOOT — check admin & fetch                                       */
  /* ================================================================ */

  useEffect(() => {
    const token = localStorage.getItem("arkila_admin_token");
    if (!token) {
      navigate("/login");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await api.get("/admin/me");
        if (cancelled) return;
        setIsAdmin(true);
        fetchAuditLogs();
      } catch (err) {
        if (cancelled) return;
        setIsAdmin(false);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================================================================ */
  /* HANDLERS                                                         */
  /* ================================================================ */

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleRoleChange = (value) => {
    setRoleFilter(value);
    setCurrentPage(1);
    fetchAuditLogs({ role: value, page: 1 });
  };

  const handleActionChange = (value) => {
    setActionFilter(value);
    setCurrentPage(1);
    fetchAuditLogs({ action: value, page: 1 });
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchAuditLogs({ page: 1, silent: true });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAuditLogs({ page });
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const handleRowKeyDown = (e, log) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openDetail(log);
    }
  };

  /* ================================================================ */
  /* PAGINATION                                                       */
  /* ================================================================ */

  const indexOfFirst = (currentPage - 1) * ITEMS_PER_PAGE;
  const indexOfLast = indexOfFirst + ITEMS_PER_PAGE;

  const renderPagination = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible)
      start = Math.max(1, end - maxVisible + 1);

    const items = [];
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  /* ================================================================ */
  /* ACCESS DENIED                                                    */
  /* ================================================================ */

  if (isAdmin === false) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Audit Logs</h2>
            <p>Restricted access</p>
          </div>
          <Row className="justify-content-center">
            <Col md={8}>
              <div className="bk-card">
                <div className="bk-empty">
                  <div
                    className="bk-empty-icon"
                    style={{ color: Colors.RED }}
                  >
                    <i className="bi bi-shield-exclamation" />
                  </div>
                  <h5 style={{ color: Colors.TEXT_DARK }}>
                    Access Denied
                  </h5>
                  <p className="text-muted mb-4">
                    {error ||
                      "You must be a Super-Admin to view audit logs."}
                  </p>
                  <Button
                    className="bk-btn-primary"
                    onClick={() => navigate("/dashboard")}
                  >
                    <i className="bi bi-arrow-left me-1" />
                    Return to Dashboard
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  /* ================================================================ */
  /* LOADING SKELETON                                                 */
  /* ================================================================ */

  if (loading && logs.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Audit Logs</h2>
            <p>Loading system activity…</p>
          </div>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="bk-skel mb-3"
                style={{ height: 42 }}
              />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  /* ================================================================ */
  /* MAIN RENDER                                                      */
  /* ================================================================ */

  return (
    <ErrorBoundary>
      <div className="bk-page">
        <Styles />
        <Container fluid>
          {/* ============ HEADER ============ */}
          <div className="bk-header">
            <div className="bk-header-actions d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h2>Audit Logs</h2>
                <p>Monitor all system activities and user actions</p>
              </div>
            </div>
          </div>

          {/* ============ ERROR BANNER ============ */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              <i className="bi bi-exclamation-triangle me-2" />
              {error}
            </Alert>
          )}

          {/* ============ TOOLBAR ============ */}
          <div className="bk-toolbar">
            <Row className="g-2">
              <Col xs={12} md={6}>
                <InputGroup>
                  <InputGroup.Text className="bk-search-icon">
                    <i className="bi bi-search" />
                  </InputGroup.Text>
                  <FormControl
                    className="bk-search"
                    placeholder="Search by actor, action, target, IP or path…"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    aria-label="Search audit logs"
                  />
                </InputGroup>
              </Col>

              <Col xs={6} md={3}>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <i className="bi bi-person-badge me-2" />
                    {ROLE_OPTIONS.find((o) => o.value === roleFilter)
                      ?.label || "All Roles"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {ROLE_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={roleFilter === o.value}
                        onClick={() => handleRoleChange(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>

              <Col xs={6} md={3}>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <i className="bi bi-lightning me-2" />
                    {ACTION_OPTIONS.find((o) => o.value === actionFilter)
                      ?.label || "All Actions"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {ACTION_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={actionFilter === o.value}
                        onClick={() => handleActionChange(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
          </div>

          {/* ============ TABLE ============ */}
          <div className="bk-card">
            <div className="bk-card-header">
              <h5>
                <i
                  className="bi bi-journal-text"
                  style={{ color: Colors.PRIMARY }}
                />
                System Activity
              </h5>
              <small>
                {totalItems > 0
                  ? `Showing ${Math.min(
                      indexOfFirst + 1,
                      totalItems
                    )}–${Math.min(indexOfLast, totalItems)} of ${totalItems}`
                  : "No records"}
              </small>
            </div>

            {logs.length === 0 ? (
              <div className="bk-empty">
                <div className="bk-empty-icon">
                  <i className="bi bi-journal-text" />
                </div>
                <h5 style={{ color: Colors.TEXT_DARK }}>
                  No audit logs found
                </h5>
                <p className="text-muted mb-0">
                  {searchQuery ||
                  roleFilter !== "All" ||
                  actionFilter !== "All"
                    ? "No logs match your filters."
                    : "There are no audit logs in the system yet."}
                </p>
              </div>
            ) : (
              <>
                <Table responsive hover className="bk-table">
                  <thead>
                    <tr>
                      <th>Date &amp; Time</th>
                      <th>Actor</th>
                      <th>Role</th>
                      <th>Action</th>
                      <th>Target</th>
                      <th>Status</th>
                      <th>IP</th>
                      <th className="col-chevron"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr
                        key={log.id || i}
                        onClick={() => openDetail(log)}
                        onKeyDown={(e) => handleRowKeyDown(e, log)}
                        tabIndex={0}
                        role="button"
                        aria-label={`View details for ${
                          log.action || "log"
                        } by ${log.actor || "unknown"}`}
                        className="bk-row-clickable"
                      >
                        <td className="text-nowrap">
                          <div style={{ fontWeight: 500 }}>
                            {formatDate(log.timestamp || log.created_at)}
                          </div>
                        </td>

                        <td>
                          <div className="bk-actor-cell">
                            <div className="bk-actor-avatar">
                              {initials(log.actor || "?")}
                            </div>
                            <div
                              className="text-truncate"
                              style={{
                                maxWidth: 160,
                                fontWeight: 600,
                                color: Colors.TEXT_DARK,
                              }}
                            >
                              {log.actor || "Unknown"}
                            </div>
                          </div>
                        </td>

                        <td>
                          <RolePill role={log.role} />
                        </td>

                        <td>
                          <ActionPill action={log.action} />
                        </td>

                        <td>
                          <span
                            className="text-truncate d-inline-block"
                            style={{
                              maxWidth: 180,
                              color: Colors.TEXT_DARK,
                            }}
                            title={log.target || "N/A"}
                          >
                            {log.target || "N/A"}
                          </span>
                        </td>

                        <td>
                          <StatusPill status={log.status} />
                        </td>

                        <td>
                          <span className="bk-mono">{log.ip || "—"}</span>
                        </td>

                        <td className="col-chevron">
                          <i className="bi bi-chevron-right text-muted" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div
                    className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{ padding: "14px 20px", borderTop: T.border }}
                  >
                    <small className="text-muted">
                      Showing {Math.min(indexOfFirst + 1, totalItems)} to{" "}
                      {Math.min(indexOfLast, totalItems)} of {totalItems}{" "}
                      entries
                    </small>
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {renderPagination()}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ============ DETAIL MODAL ============ */}
          <Modal
            show={detailOpen}
            onHide={() => setDetailOpen(false)}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: `${Colors.PRIMARY}1f`,
                      color: Colors.PRIMARY,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <i className="bi bi-journal-text" />
                  </span>
                  Audit Log Detail
                  {selectedLog?.id && (
                    <span className="bk-id-chip ms-2">
                      #{selectedLog.id}
                    </span>
                  )}
                </div>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {selectedLog && (
                <>
                  {/* Activity */}
                  <div className="bk-section">
                    <div className="bk-section-title">
                      <i className="bi bi-info-circle" />
                      Activity
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Timestamp</span>
                      <span className="bk-info-value">
                        {formatDate(
                          selectedLog.timestamp || selectedLog.created_at
                        )}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Actor</span>
                      <span className="bk-info-value">
                        {selectedLog.actor || "—"}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Role</span>
                      <span className="bk-info-value">
                        <RolePill role={selectedLog.role} />
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Action</span>
                      <span className="bk-info-value">
                        <ActionPill action={selectedLog.action} />
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Status</span>
                      <span className="bk-info-value">
                        <StatusPill status={selectedLog.status} />
                      </span>
                    </div>
                  </div>

                  {/* Target */}
                  <div className="bk-section">
                    <div className="bk-section-title">
                      <i className="bi bi-crosshair" />
                      Target
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Target</span>
                      <span className="bk-info-value">
                        {selectedLog.target || "—"}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Target Type</span>
                      <span className="bk-info-value">
                        {selectedLog.target_type || "—"}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Target ID</span>
                      <span className="bk-info-value mono">
                        {selectedLog.target_id || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Request */}
                  <div className="bk-section">
                    <div className="bk-section-title">
                      <i className="bi bi-hdd-network" />
                      Request Info
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">IP Address</span>
                      <span className="bk-info-value mono">
                        {selectedLog.ip || "—"}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Method</span>
                      <span className="bk-info-value mono">
                        {selectedLog.method || "—"}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">Request Path</span>
                      <span className="bk-info-value mono">
                        {selectedLog.request_path || "—"}
                      </span>
                    </div>
                    <div className="bk-info-row">
                      <span className="bk-info-label">User Agent</span>
                      <span
                        className="bk-info-value"
                        style={{ fontSize: 12 }}
                      >
                        {selectedLog.user_agent || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Metadata */}
                  {selectedLog.metadata && (
                    <div className="bk-section" style={{ marginBottom: 0 }}>
                      <div className="bk-section-title">
                        <i className="bi bi-braces" />
                        Metadata
                      </div>
                      <pre className="bk-pre">
                        {safeStringify(selectedLog.metadata)}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button
                className="bk-btn-ghost"
                onClick={() => setDetailOpen(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </div>
    </ErrorBoundary>
  );
};

export default AuditLogs;